'use client';

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { Table } from '@benzinga/table';
import { InsiderTradesManager, type TopExecRow, type InsiderOptions } from '@benzinga/insider-trades-manager';

import { PermissionOverlay } from '../../../edge/dashboard/components/PermissionOverlay';
import { getTopExecsColumnDefs } from './columnDefs/topExecsColumnsDefs';
import {
  INSIDER_ROLE_OPTIONS,
  INSIDER_TIME_PERIOD_OPTIONS,
  type InsiderRoleFilterValue,
  TRADE_TYPE_OPTIONS,
} from './filterOptions';
import { InsiderFilterSelect } from './InsiderFilterSelect';
import { useInsiderEdgeGate } from './useInsiderEdgeGate';

import styles from '../../insiderTrades.module.scss';
import '../../table.scss';
import './agGridRegister';

const PERIOD_SELECT_OPTIONS = INSIDER_TIME_PERIOD_OPTIONS.map(o => ({
  value: String(o.value),
  label: o.label,
}));

export function TopExecsTable() {
  const [period, setPeriod] = useState(30);
  const [transactionType, setTransactionType] = useState('all');
  const [insiderRole, setInsiderRole] = useState<InsiderRoleFilterValue>('all');
  const [rows, setRows] = useState<TopExecRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const session = useContext(SessionContext);
  const insiderTradesManager = session.getManager(InsiderTradesManager);
  const { canLoadGatedData, isPaywalled } = useInsiderEdgeGate();

  const fetchTopExecs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const options: InsiderOptions = {
      period,
      trade_types: transactionType !== 'all' ? transactionType.split(',').map(t => t.trim()) : undefined,
      ...(insiderRole !== 'all' ? { insider_role: insiderRole } : {}),
    };
    try {
      const result = await insiderTradesManager.getTopExecs(options);
      if (result.ok) {
        setRows(result.ok.data ?? []);
      } else {
        setError(result.err?.message ?? 'Failed to load');
        setRows([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period, transactionType, insiderRole, insiderTradesManager]);

  useEffect(() => {
    if (isPaywalled) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (!canLoadGatedData) return;
    fetchTopExecs();
  }, [canLoadGatedData, fetchTopExecs, isPaywalled]);

  const columnDefs = useMemo(() => getTopExecsColumnDefs(), []);

  const tableBody = (
    <div
      className={`${styles['insider-table-section-body']} ${styles['insider-trades-table-wrapper']} insider-trades-table-wrapper`}
    >
      {error && (
        <div className={styles['top-companies-empty']} role="alert">
          {error}
        </div>
      )}
      {loading && rows.length === 0 ? (
        <div className={styles['top-companies-empty']}>Loading…</div>
      ) : (
        <>
          <Table<TopExecRow>
            rowData={rows}
            columnsDef={columnDefs as React.ComponentProps<typeof Table<TopExecRow>>['columnsDef']}
            isGated={isPaywalled}
            gatedRowBlur="row"
            gatedVisibleRowCount={0}
            noDataText={isPaywalled ? 'Unlock Benzinga Edge to view insider trades.' : undefined}
          />
        </>
      )}
    </div>
  );

  return (
    <div className={styles['insider-table-section']}>
      <div className={styles['insider-table-section-header']}>
        <h3 className={styles['section-title']}>Leaderboard (Top 100 Execs)</h3>
        <div className={styles['insider-table-filters']}>
          <InsiderFilterSelect
            id="top-execs-period"
            label="Time Period"
            ariaLabel="Time period"
            value={String(period)}
            disabled={isPaywalled}
            onValueChange={v => setPeriod(Number(v))}
            options={PERIOD_SELECT_OPTIONS}
          />
          <InsiderFilterSelect
            id="top-execs-transaction"
            label="Transaction"
            ariaLabel="Transaction type"
            value={transactionType}
            disabled={isPaywalled}
            onValueChange={setTransactionType}
            options={TRADE_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          />
          <InsiderFilterSelect
            id="top-execs-role"
            label="Insider Role"
            ariaLabel="Insider role"
            value={insiderRole}
            disabled={isPaywalled}
            onValueChange={v => setInsiderRole(v as InsiderRoleFilterValue)}
            options={INSIDER_ROLE_OPTIONS}
          />
        </div>
      </div>

      <PermissionOverlay>{tableBody}</PermissionOverlay>
    </div>
  );
}
